import React from "react";
import styled from "styled-components";
import { eventEmitter, i18n, navigate } from "../../../services";
import {
    hasSelection,
    getSelectionRange,
    setSelectionRange,
    EVT_MOUSEUP,
    EVT_MOUSEDOWN,
    EVT_KEYUP,
    EVT_KEYDOWN,
    EVT_DBLCLICK,
} from "../../../lib";
import { isShortcut, S_ARABIC, S_NEW_PAGE } from "./tools/shortcuts";
import {
    createLink,
    HIDE_TOOLBAR,
    isAlignCenter,
    isAlignLeft,
    isAlignRight,
    isBold,
    isIndentDecrease,
    isIndentIncrease,
    isItalic,
    isListOrdered,
    isListUnOrdered,
    isUnderline,
    onArabicLang,
} from "./tools/commands";
import { getPosition } from "./helpers";
import { ID_EDITOR_CONTENTS } from "../Contents/styles";

import tools from "./tools";

const OFFSET_TOP = 50;

const Container = styled.div<{
    show?: boolean;
    top: number;
    right: number;
    enableDarkTheme?: boolean;
}>`
    visibility: ${(p) => (p.show ? "visible" : "hidden")};
    z-index: 10;
    user-select: none;
    position: absolute;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    height: 30px;
    right: ${(p) => p.right}px;
    top: ${(p) => p.top - OFFSET_TOP}px;
    border-radius: 2px;
    color: ${(p) => (p.enableDarkTheme ? "#000" : "#fff")};
    background-color: ${(p) => (p.enableDarkTheme ? "#fff" : "#000")};
`;
const Group = styled.div<{
    enableDarkTheme?: boolean;
}>`
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    padding: 0 7px;
    height: 30px;

    &:empty {
        display: none;
    }

    &:after {
        content: " ";
        position: absolute;
        left: 0;
        top: 0;
        width: 1px;
        height: 100%;
        background-color: ${(p) => (p.enableDarkTheme ? "#000" : "#fff")};
    }

    &:last-child:after {
        display: none;
    }

    img {
        cursor: pointer;
        width: 24px;
        height: 24px;
    }
`;

export const CTX_ALL = "ctx/active";
export const CTX_MORE = "ctx/more";
export const CTX_LINK = "ctx/link";
export const CTX_ERROR = "ctx/error";
export const CTX_TEXT_SIZE = "ctx/textSize";

type Props = {
    doc_id: number;
    page_id: number;
    parent_id: number;
    enableDarkTheme: boolean;
};

type Tool = {
    [x: string]: any;
    name: string;
    show: boolean;
    context?: string | undefined;
};
type State = {
    show: boolean;
    right: number;
    top: number;
    error: string | null;
    context: string | null;
    shortcut: string | null;
    view: Array<Array<Tool>>;
};

export class Toolbar extends React.Component<Props, State> {
    private elmContents: HTMLDivElement | null = null;
    private savedSelectionRange: ReturnType<typeof getSelectionRange> = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            show: false,
            right: 0,
            top: 0,
            error: null,
            context: null,
            shortcut: null,

            view: [
                [
                    { name: "bold", show: true },
                    { name: "italic", show: true },
                    { name: "underline", show: true },
                ],
                [
                    { name: "textSize", show: true },
                    {
                        name: "h1",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                    {
                        name: "h2",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                    {
                        name: "h3",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                    {
                        name: "h4",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                    {
                        name: "h5",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                    {
                        name: "h6",
                        show: false,
                        context: CTX_TEXT_SIZE,
                    },
                ],
                [
                    { name: "alignRight", show: true },
                    { name: "alignCenter", show: true },
                    { name: "alignLeft", show: true },
                ],
                [
                    { name: "quote", show: true, context: CTX_MORE },
                    { name: "sourceCode", show: true, context: CTX_MORE },
                ],
                [
                    { name: "ol", show: true },
                    { name: "ul", show: true },
                ],
                [
                    { name: "increaseIndent", show: true },
                    { name: "decreaseIndent", show: true },
                ],
                [
                    { name: "link", show: true },
                    {
                        name: "linkField",
                        show: true,
                        width: 300,
                        context: CTX_LINK,
                        textAlign: "left",
                        placeholder: i18n.get("toolbar.linkPlaceholder"),
                    },
                    { name: "image", show: true },
                    /*{ name: 'video', show: true, width: 20 },*/
                ],
                [
                    { name: "rtl", show: true, context: CTX_MORE },
                    { name: "ltr", show: true, context: CTX_MORE },
                ],
                [
                    {
                        name: "removeFormat",
                        show: true,
                        context: CTX_MORE,
                    },
                ],
                [
                    {
                        name: "errorMsg",
                        show: true,
                        width: 330,
                        context: CTX_ERROR,
                        msg: "",
                    },
                    { name: "more", show: true },
                    { name: "back", show: false, context: CTX_ALL },
                ],
            ],
        };
    }

    componentDidMount() {
        this.elmContents = document.getElementById(
            ID_EDITOR_CONTENTS
        ) as HTMLDivElement;

        if (!this.elmContents) {
            return;
        }

        this.elmContents.addEventListener(EVT_MOUSEDOWN, this.onMouseDown);
        this.elmContents.addEventListener(EVT_MOUSEUP, this.onMouseUp);
        this.elmContents.addEventListener(EVT_KEYUP, this.onKeyUp);
        this.elmContents.addEventListener(EVT_KEYDOWN, this.onKeyDown);
        this.elmContents.addEventListener(EVT_DBLCLICK, this.onDblClick);
        eventEmitter.on(HIDE_TOOLBAR, this.onHide);
    }

    componentWillUnmount() {
        if (!this.elmContents) {
            return;
        }

        this.elmContents.removeEventListener(EVT_MOUSEDOWN, this.onMouseDown);
        this.elmContents.removeEventListener(EVT_MOUSEUP, this.onMouseUp);
        this.elmContents.removeEventListener(EVT_KEYUP, this.onKeyUp);
        this.elmContents.removeEventListener(EVT_KEYDOWN, this.onKeyDown);
        this.elmContents.removeEventListener(EVT_DBLCLICK, this.onDblClick);
        eventEmitter.off(HIDE_TOOLBAR, this.onHide);

        this.elmContents = null;
    }

    onMouseDown = () => {
        this.setState({ show: false });
        this.clearContext();
    };

    onMouseUp = () => {
        if (hasSelection()) {
            const pos = getPosition();
            if (pos) {
                const { right, top } = pos;
                this.setState({
                    top,
                    right: window.innerWidth - right,
                    show: true,
                });
            }
        } else {
            this.setState({
                show: false,
            });
            this.clearContext();
        }
    };

    onKeyUp = () => {
        this.clearContext();

        const pos = getPosition();
        if (pos) {
            const { right, top } = pos;
            this.setState({
                top,
                right: window.innerWidth - right,
                show: hasSelection(),
            });
        }
    };

    onKeyDown = (evt: KeyboardEvent) => {
        const { code, context } = isShortcut(evt);
        if (code) {
            this.editorActions(code);

            context && this.setContext(context);
            this.setState({ shortcut: code }, () =>
                this.setState({ shortcut: null })
            );

            evt.preventDefault();
            return false;
        }

        this.clearContext();

        const pos = getPosition();
        if (pos) {
            const { right, top } = pos;
            this.setState({
                top,
                right: window.innerWidth - right,
                show: hasSelection(),
            });
        }
    };

    onDblClick = (evt: MouseEvent) => {
        this.show(evt);
    };

    editorActions = (shortcut: string) => {
        const { doc_id, parent_id } = this.props;
        if (shortcut === S_NEW_PAGE) {
            navigate.to(
                `/app/pages/${doc_id}/create?redirect=pages/EDITOR&parent_id=${parent_id}`
            );
        } else if (shortcut === S_ARABIC) {
            onArabicLang();
        }
    };

    show = (evt: MouseEvent) => {
        this.clearContext();
        this.setState({
            show: true,
            right: window.innerWidth - evt.clientX,
            top: evt.clientY,
        });
    };

    hide = () => {
        this.setState({ show: false });
        this.clearContext();

        if (this.savedSelectionRange) {
            setSelectionRange(this.savedSelectionRange);
            this.savedSelectionRange = null;
        }
    };

    onHide = () => this.hide();

    onTextSize = () => this.setContext(CTX_TEXT_SIZE);
    onMore = () => this.setContext(CTX_MORE);

    onSetErrorMsg = (msgId: string) => {
        this.setState({ error: i18n.get(msgId) });
        this.setContext(CTX_ERROR);
    };

    onLink = () => {
        this.savedSelectionRange = getSelectionRange();
        this.setContext(CTX_LINK);
    };

    onSetLink = (link: string) => {
        if (!this.savedSelectionRange) {
            return;
        }

        setSelectionRange(this.savedSelectionRange);
        this.savedSelectionRange = null;
        createLink(link);
    };

    canShow(t: Tool, context: string) {
        if (!t.context) {
            return false;
        }

        return t.context === context || t.context === CTX_ALL;
    }

    setContext = (context: string) => {
        this.setState({ context });

        const { view } = this.state;
        this.setState({
            view: view.map((group) =>
                group.map((t) => ({
                    ...t,
                    show: this.canShow(t, context),
                }))
            ),
        });
    };

    clearContext = () => {
        const { view } = this.state;
        const cmdIndent = ["increaseIndent", "decreaseIndent"];
        const cmdAlign = ["alignLeft", "alignRight", "alignCenter"];

        this.setState({
            context: null,
            error: null,

            view: view.map((group) =>
                group.map((t) => {
                    let show = !t.context;

                    if (
                        cmdIndent.includes(t.name) &&
                        !isListOrdered() &&
                        !isListUnOrdered()
                    ) {
                        show = false;
                    } else if (cmdAlign.includes(t.name)) {
                        if (t.name === "alignRight" && isAlignRight()) {
                            show = false;
                        } else if (
                            t.name === "alignCenter" &&
                            isAlignCenter()
                        ) {
                            show = false;
                        } else if (t.name === "alignLeft" && isAlignLeft()) {
                            show = false;
                        }
                    }

                    return {
                        ...t,
                        show,
                    };
                })
            ),
        });
    };

    private contextFn: Record<string, (() => void) | ((x: string) => void)> = {
        textSize: this.onTextSize,
        more: this.onMore,
        link: this.onLink,
        linkField: this.onSetLink,
        back: this.clearContext,
    };

    private enabledFn: Record<string, () => boolean> = {
        bold: isBold,
        italic: isItalic,
        underline: isUnderline,
        alignRight: isAlignRight,
        alignCenter: isAlignCenter,
        alignLeft: isAlignLeft,
        ol: isListOrdered,
        ul: isListUnOrdered,
        decreaseIndent: isIndentDecrease,
        increaseIndent: isIndentIncrease,
    };

    render() {
        const { doc_id, page_id, enableDarkTheme } = this.props;
        const { view, error, shortcut, show, top, right } = this.state;

        return (
            <Container
                show={show}
                top={top}
                right={right}
                enableDarkTheme={enableDarkTheme}
            >
                {view.map((o, i) => {
                    return (
                        <Group
                            key={`group-${i}`}
                            enableDarkTheme={enableDarkTheme}
                        >
                            {o.map((p) => {
                                const errorMsg =
                                    p.context === CTX_ERROR && error
                                        ? error
                                        : null;

                                if (!p.show) {
                                    return null;
                                }

                                const enabled = this.enabledFn[p.name]
                                    ? this.enabledFn[p.name]()
                                    : false;

                                const Component = tools[p.name];
                                return (
                                    <Component
                                        {...p}
                                        key={`tool-${p.name}`}
                                        shortcut={shortcut}
                                        enabled={!enabled}
                                        doc_id={doc_id}
                                        page_id={page_id}
                                        enableDarkTheme={enableDarkTheme}
                                        onClick={this.contextFn[p.name] || null}
                                        errorMsg={errorMsg}
                                        setErrorMsg={this.onSetErrorMsg}
                                    />
                                );
                            })}
                        </Group>
                    );
                })}
            </Container>
        );
    }
}
